// Script builder state (in memory only, like the old client).
import * as api from '$api/endpoints.js';

/** @typedef {{ product_id: number, dosage_notes: string, show: boolean, quantity: number, price: number, product: any, margin_reduction?: number, discounted_price?: number, discount_percentage?: number }} ScriptLine */

class ScriptStore {
	/** @type {ScriptLine[]} */
	lines = $state([]);
	patientName = $state('');
	patientEmail = $state('');
	patientId = $state(0);
	/** Name and email as they were when `patientId` was chosen (see `confirmedPatientId`). */
	#chosenWith = { name: '', email: '' };
	additionalInstructions = $state('');
	/** uid of the last created script (empty while building a new one). */
	uid = $state('');

	/**
	 * Upsert a line from a product (cart line or `products/get.json` product).
	 * @param {any} product
	 * @param {string} [dosageNotes]
	 */
	updateLine(product, dosageNotes = '') {
		const line = this.lines.find((l) => l.product_id === product.id);
		if (line) {
			line.quantity = product.cartCount;
			line.price = product.retail_price.price;
			return line;
		}
		const newLine = {
			product_id: product.id,
			dosage_notes: dosageNotes,
			show: dosageNotes != '',
			quantity: product.cartCount || parseInt(product.qty),
			price: product.retail_price.price,
			// MarginSlider binds this and has a fallback, so it must never be undefined
			margin_reduction: 0,
			product: $state.snapshot(product)
		};
		this.lines.push(newLine);
		return this.lines[this.lines.length - 1];
	}

	/**
	 * Keep only the lines whose product ids are listed.
	 * @param {Array<number>} [except]
	 */
	clearLines(except = []) {
		this.lines = this.lines.filter((l) => except.includes(l.product_id));
	}

	/** @param {number} product_id */
	removeLine(product_id) {
		this.lines = this.lines.filter((l) => l.product_id !== product_id);
	}

	/**
	 * @param {number} product_id
	 * @param {number} [count]
	 * @param {string} [dosageNotes]
	 */
	async addProduct(product_id, count = 1, dosageNotes = '') {
		const response = await api.getProduct(product_id, count);
		if (response && response.product) {
			this.updateLine(response.product, dosageNotes);
		}
	}

	/**
	 * Build the script from cart lines. Events (type 5) are skipped silently; unscriptable
	 * products are skipped and returned so the caller can warn.
	 * @param {any[]} cartItems
	 * @returns {string[]} names of products that could not be scripted
	 */
	fromCart(cartItems) {
		this.uid = '';
		this.clearLines(cartItems.map((p) => p.id));
		/** @type {string[]} */
		const skipped = [];
		for (const product of cartItems) {
			if (product.product_type == 5) continue;
			if (product.unscriptable) {
				skipped.push(product.name);
				continue;
			}
			this.updateLine(product, product.dosage_notes_for_script || '');
		}
		return skipped;
	}

	/**
	 * Use an existing patient (picked from the lookup or carried over from a prescribed script).
	 * @param {number} id
	 * @param {string} name
	 * @param {string} email
	 */
	setPatient(id, name, email) {
		this.patientId = id || 0;
		this.patientName = name ?? '';
		this.patientEmail = email ?? '';
		this.#chosenWith = { name: this.patientName, email: this.patientEmail };
	}

	/**
	 * The patient id to create the script for: the chosen patient only while the name and email
	 * are still the ones it was chosen with. Once either is edited the details belong to someone
	 * else, so 0 asks the backend to find or create the patient from them.
	 */
	get confirmedPatientId() {
		/** @param {string} value */
		const normalise = (value) =>
			String(value ?? '')
				.trim()
				.toLowerCase();
		const unchanged =
			normalise(this.patientName) === normalise(this.#chosenWith.name) &&
			normalise(this.patientEmail) === normalise(this.#chosenWith.email);
		return unchanged ? this.patientId : 0;
	}

	resetPatient() {
		this.setPatient(0, '', '');
	}

	reset() {
		this.lines = [];
		this.additionalInstructions = '';
		this.uid = '';
		this.resetPatient();
	}
}

export const script = new ScriptStore();
